export type LeadStatus =
    | 'new'
    | 'approached'
    | 'no_answer'
    | 'not_interested'
    | 'scheduled_calls'
    | 'qualified'
    | 'not_qualified'
    | 'reserved'
    | 'reserved_with_deposit'
    | 'sold'
    | 'contracted'
    | 'notes';

export const LEAD_STATUS_OPTIONS: LeadStatus[] = [
    'new',
    'approached',
    'no_answer',
    'not_interested',
    'scheduled_calls',
    'qualified',
    'not_qualified',
    'reserved',
    'reserved_with_deposit',
    'sold',
    'contracted',
    'notes'
];

export const LEAD_STAT_TILE_STATUSES: LeadStatus[] = LEAD_STATUS_OPTIONS.filter(
    (status) => !['approached', 'scheduled_calls', 'notes'].includes(status)
);

export function normalizeLeadStatus(value: unknown): LeadStatus {
    const raw = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
    if (raw === 'new') return 'new';
    if (raw === 'contacted' || raw === 'approached') return 'approached';
    if (['noanswer', 'unanswered', 'noresponse', 'didnotanswer'].includes(raw)) return 'no_answer';
    if (['notinterested', 'uninterested', 'lost'].includes(raw)) return 'not_interested';
    if (['scheduledcalls', 'scheduledcall', 'scheduledcallback', 'followupscheduled', 'callbackscheduled'].includes(raw)) {
        return 'scheduled_calls';
    }
    if (['qualified', 'negotiating', 'negotiation'].includes(raw)) return 'qualified';
    // Exact-key match, so no collision with 'qualified' above or 'not_interested'
    // above: not_interested is the lead declining, not_qualified is us
    // disqualifying the lead. Mirrors API2/lib/leadStatus.js.
    if (['notqualified', 'unqualified', 'disqualified'].includes(raw)) return 'not_qualified';
    if (['reserved', 'reserve', 'hold', 'held', 'booking', 'booked', 'reservedhold', 'reservedorhold'].includes(raw)) {
        return 'reserved';
    }
    if (['reservedwithdeposit', 'reserveddeposit', 'depositreserved'].includes(raw)) return 'reserved_with_deposit';
    if (['sold', 'closed', 'completed'].includes(raw)) return 'sold';
    if (['contracted', 'contract'].includes(raw)) return 'contracted';
    if (['notes', 'note', 'noted', 'followupnotes', 'remark', 'remarks'].includes(raw)) return 'notes';
    return 'new';
}

// Single source of truth for lead status colour, mirroring the dashboard's
// statusAccentByKey(): every lead surface (stat cards, list pills, filters)
// derives from this map so a colour cannot drift between them.
//
// These are the nibni brand accents, NOT the Tailwind palette:
//   #6E9BAD  slate-blue  — untouched / scheduled (informational)
//   #AA9072  brand gold  — in progress (--nibni-light-taupe)
//   #B88746  deep gold   — committed (qualified / reserved)
//   #C65A5A  red         — lost, the lead declined
//   #8A7E7E  warm grey   — disqualified by us (deliberately distinct from red)
//   #3A4E49  dark teal   — closed / neutral (--nibni-dark-teal)
export const LEAD_STATUS_ACCENTS: Readonly<Record<LeadStatus, string>> = Object.freeze({
    new: '#6E9BAD',
    approached: '#AA9072',
    no_answer: '#AA9072',
    not_interested: '#C65A5A',
    scheduled_calls: '#6E9BAD',
    qualified: '#B88746',
    not_qualified: '#8A7E7E',
    reserved: '#B88746',
    reserved_with_deposit: '#B88746',
    sold: '#3A4E49',
    contracted: '#3A4E49',
    notes: '#3A4E49'
});

// Every name here must be registered in shared/lucide-icons.module.ts —
// lucide-angular renders nothing for an unknown name and only warns.
const LEAD_STATUS_ICONS: Readonly<Record<LeadStatus, string>> = Object.freeze({
    new: 'sparkles',
    approached: 'phone-call',
    no_answer: 'bell-off',
    not_interested: 'x-circle',
    scheduled_calls: 'calendar-clock',
    qualified: 'badge-check',
    not_qualified: 'ban',
    reserved: 'bookmark',
    reserved_with_deposit: 'wallet',
    sold: 'check-check',
    contracted: 'file-signature',
    notes: 'message-square'
});

export function leadStatusAccent(value: unknown): string {
    return LEAD_STATUS_ACCENTS[normalizeLeadStatus(value)];
}

export function leadStatusIcon(value: unknown): string {
    return LEAD_STATUS_ICONS[normalizeLeadStatus(value)];
}

export function hexToRgba(hex: string, alpha: number): string {
    const value = String(hex || '').replace('#', '');
    const r = Number.parseInt(value.slice(0, 2), 16);
    const g = Number.parseInt(value.slice(2, 4), 16);
    const b = Number.parseInt(value.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function leadStatusLabel(value: unknown): string {
    // Held units keep the stored status 'hold' (aliased into the reserved
    // family for counting/colours) — surfaces must render it as "On Hold",
    // per product spec. Matched on the raw value before normalization.
    const raw = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
    if (['hold', 'held', 'onhold'].includes(raw)) return 'On Hold';
    switch (normalizeLeadStatus(value)) {
        case 'approached':
            return 'Approached';
        case 'no_answer':
            return 'No Answer';
        case 'not_interested':
            return 'Not Interested';
        case 'scheduled_calls':
            return 'Scheduled Calls';
        case 'qualified':
            return 'Qualified';
        case 'not_qualified':
            return 'Not Qualified';
        case 'reserved':
            return 'Reserved';
        case 'reserved_with_deposit':
            return 'Reserved with Deposit';
        case 'sold':
            return 'Sold';
        case 'contracted':
            return 'Contracted';
        case 'notes':
            return 'Notes';
        case 'new':
        default:
            return 'New';
    }
}
